import { sendEvent } from "../00-atoms/00-global/EventHelpers";
import { getDocument, GetViewPortSize, Throttle } from "../00-atoms/00-global/Utils";
import { InitSwipeEvent, SwipeDetail, SwipeDirections } from "./swiped-events";

interface MainNavElements {
  mainNav: HTMLElement;
  mainNavToggle: NodeListOf<Element>;
  showWhenMainNavIsVisibleMobil: NodeListOf<Element>;
  hideWhenMainNavIsVisible: NodeListOf<Element>;
  searchInput: HTMLElement;
}

interface MainNavItem {
  mainNavIconContainer: Element;
  iconOpen: HTMLElement;
  iconClose: HTMLElement;
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace MainNav {
  let _transition = "";
  let _previousScrollPos = 0;
  let _open = false;
  function getTransitionEnd(el: HTMLElement): string {
    if (!el) return "";

    const transitions: { [index: string]: string } = {
      transition: "transitionend",
      OTransition: "oTransitionEnd",
      MozTransition: "transitionend",
      WebkitTransition: "webkitTransitionEnd",
    };

    const style = el.style;
    for (const key in transitions) {
      // https://github.com/Microsoft/TypeScript/issues/17827
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const transitionExists: string = (style as any)[key];
      if (transitionExists !== null) {
        return transitions[key];
      }
    }

    return "";
  }

  function mobileShowHide(mainNavElem: MainNavElements, show: boolean) {
    const viewport = GetViewPortSize();
    if (viewport.x < 770 && show) {
      mainNavElem.showWhenMainNavIsVisibleMobil.forEach((el: Element): void => {
        (el as HTMLElement).style.display = "block";
      });
      return;
    }
    mainNavElem.showWhenMainNavIsVisibleMobil.forEach((el: Element): void => {
      (el as HTMLElement).style.display = "none";
    });
  }

  export function setup(event: Event) {
    const doc = getDocument(event as CustomEvent);

    const mainNavElem: MainNavElements = {
      mainNav: doc.getElementById("js-main-nav"),
      mainNavToggle: doc.querySelectorAll(".js-main-nav-toggle"),
      showWhenMainNavIsVisibleMobil: doc.querySelectorAll(".js-show-when-main-nav-visible-mobil"),
      hideWhenMainNavIsVisible: doc.querySelectorAll(".js-hide-when-main-nav-visible"),
      searchInput: doc.getElementById("search"),
    };

    _transition = getTransitionEnd(mainNavElem.mainNav);

    if (!mainNavElem.mainNavToggle || !mainNavElem.mainNav) return;
    mainNavElem.mainNavToggle.forEach((el) => {
      const currentItem: MainNavItem = {
        mainNavIconContainer: el,
        iconOpen: el.querySelector(".nav-icon-open"),
        iconClose: el.querySelector(".nav-icon-close"),
      };

      mainNavElem.mainNav.addEventListener(_transition, function () {
        if (currentItem.iconClose.classList.contains("u-hidden")) {
          mainNavElem.mainNav.style.display = "none";
          setTimeout(() => sendEvent(mainNavElem.mainNav, "mainNavCollaps"));
        }
      });

      currentItem.mainNavIconContainer.addEventListener("click", (e) => {
        e.stopImmediatePropagation();
        e.preventDefault();

        sendEvent(mainNavElem.mainNav, "mainNavChangeStart");

        window.addEventListener(
          "scroll",
          Throttle(() => this.hideMenuOnScroll(mainNavElem, currentItem), 100),
        );

        const removeSwipeEvents = InitSwipeEvent(mainNavElem.mainNav);

        const SwipedCloseEvent = ((e: CustomEvent<SwipeDetail>) => {
          if (e.detail.dir === SwipeDirections.SwipeLeft || e.detail.dir === SwipeDirections.SwipeRight) {
            if (mainNavElem.mainNav.classList.contains("main-nav--visible")) {
              this.hideMenu(mainNavElem, currentItem);
              removeSwipeEvents();
              mainNavElem.mainNav.removeEventListener("swiped", SwipedCloseEvent);
            }
          }
        }) as EventListener;

        mainNavElem.mainNav.addEventListener("swiped", SwipedCloseEvent, { passive: true });

        window.addEventListener("ice-seachmenu--opening", () => {
          if (mainNavElem.mainNav.classList.contains("main-nav--visible")) {
            this.hideMenu(mainNavElem, currentItem);
          }
        });

        if (mainNavElem.mainNav.classList.contains("main-nav--visible")) {
          this.hideMenu(mainNavElem, currentItem);
        } else {
          this.showMenu(mainNavElem, currentItem);
        }
      });
    });
  }

  export function hideMenuOnScroll(mainNavElem: MainNavElements, currentItem: MainNavItem) {
    if (_open) {
      if (_previousScrollPos === 0) {
        _previousScrollPos = window.pageYOffset;
      }

      if (Math.abs(window.pageYOffset - _previousScrollPos) > mainNavElem.mainNav.offsetHeight) {
        this.hideMenu(mainNavElem, currentItem);
      }
    }
  }

  export function showMenu(mainNavElem: MainNavElements, currentItem: MainNavItem) {
    mainNavElem.mainNav.style.display = "flex";
    mobileShowHide(mainNavElem, true);

    currentItem.mainNavIconContainer.setAttribute("aria-expanded", "true");
    currentItem.iconClose.classList.remove("u-hidden");
    currentItem.iconOpen.classList.add("u-hidden");

    mainNavElem.hideWhenMainNavIsVisible.forEach((el: Element): void => {
      el.classList.add("main-nav__hideout-content");
    });

    setTimeout(() => {
      mainNavElem.mainNav.classList.add("main-nav--visible");
      _open = true;
    }, 50);

    setTimeout(() => mainNavElem.searchInput.focus(), 150);
  }

  export function hideMenu(mainNavElem: MainNavElements, currentItem: MainNavItem) {
    _previousScrollPos = 0;
    _open = false;

    currentItem.mainNavIconContainer.setAttribute("aria-expanded", "false");

    setTimeout(function () {
      currentItem.iconClose.classList.add("u-hidden");
      currentItem.iconOpen.classList.remove("u-hidden");

      mainNavElem.hideWhenMainNavIsVisible.forEach((el: Element) => el.classList.remove("main-nav__hideout-content"));
      mobileShowHide(mainNavElem, false);

      mainNavElem.mainNav.classList.remove("main-nav--visible");
    }, 50);
  }

  export function init() {
    document.addEventListener("DOMContentLoaded", (e) => this.setup(e));
    document.addEventListener("__ice_InitStandalone", (e) => this.setup(e));
  }
}
